// QA/preview shortcut: `?debug=<key>` force-assigns an experiment arm, seeds a
// qualifying survey profile into localStorage, and jumps to the screen that
// experiment changes. Optional `&arm=<arm>` overrides the forced arm (defaults
// to "variation"; pass "control" to preview the control side).
//
// Examples:
//   /?debug=web29_trial_toggle           -> pricing page, no-trial toggle variant
//   /?debug=web28_male_weight_gain_effects -> the male weight-gain-effects screen
//   /?debug=web28&arm=control            -> same trigger, control arm (screen hidden)
//   /?debug=<any_experiment_name>        -> just force that experiment, no seeding
//
// To force an experiment WITHOUT seeding/jumping (walk the quiz normally with it
// on), use `?force=<experiment>` instead (optional `&arm=`):
//   /?force=web28_male_weight_gain_effects
//   /?force=web29_trial_toggle&arm=control
//
// Forcing relies on getAssignedExperimentArm honoring the forceExperiment /
// forceArm localStorage keys (same mechanism used in production), so no Statsig
// assignment is needed locally. Safe to ship: it only acts when ?debug is passed
// and only writes to the current browser's localStorage.

import { LOCAL_STORAGE_KEY } from "./App";

// A fully completed quiz so `/results` rehydrates and the chart renders.
const FULL_DEMO_PROFILE: Record<string, unknown> = {
  goals: "Lose Weight",
  gender: "Male",
  age: "30-39",
  heightUnit: "ft",
  heightRaw: 5,
  heightInches: 10,
  weight: 180,
  weightUnit: "lbs",
  goalWeight: 160,
  name: "Preview",
  bodytype: "Skinny",
  bodyFat: 35,
  describeSelf: "DietAndTraining",
  areaBody: ["Belly", "Love Handles", "Chest"],
  muscleFocus: "Chest",
  experience: "Beginner",
  email: "preview@example.com",
  duration: "0-6 months",
  motivation: "Confidence",
  shame: "Often",
  dissatisfaction: 3,
  consistency: 3,
  diet: 3,
  otherPrograms: ["OnlineFitnessProgram"],
  workouttrackingapp: "Yes",
  nutritiontrackingapp: "Yes",
  science: "Very",
  activity: "sedentary",
  cardio: "Rarely",
  comfort: "Never",
  availableDays: ["Monday", "Tuesday", "Wednesday"],
  numberWorkouts: "3",
  timeAvailable: "60",
  togetherConsistent: "false",
  focus: "Nutrition",
};

type DebugScenario = {
  /** The Statsig experiment key to force. */
  experiment: string;
  /** Short aliases accepted in place of the full experiment key. */
  aliases?: string[];
  /** Survey answers to seed so the screen's trigger is satisfied. */
  data: Record<string, unknown>;
  /** Quiz page to land on (resolved by name against the live model). */
  currentPageName: string;
  /** If set, redirect here (e.g. "/results") instead of staying in the quiz. */
  redirectTo?: string;
};

const SCENARIOS: DebugScenario[] = [
  {
    experiment: "web29_trial_toggle",
    aliases: ["pricing", "web29"],
    data: FULL_DEMO_PROFILE,
    currentPageName: "focus",
    redirectTo: "/results",
  },
  {
    experiment: "web28_male_weight_gain_effects",
    aliases: ["web28"],
    // Just enough to satisfy the trigger: male, 30+, losing weight.
    data: { goals: "Lose Weight", gender: "Male", age: "30-39" },
    currentPageName: "weightGainEffects",
  },
];

export function maybeActivateDebugExperiment(): void {
  try {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const arm = params.get("arm") || "variation";

    // `?force=<experiment>` just flips the experiment on (no seeding, no jump) so
    // you can walk the quiz normally with it active. Accepts a comma-separated
    // list to force several at once, e.g. ?force=web24_pricing_extras,web29_trial_toggle.
    const forceKey = params.get("force");
    if (forceKey) {
      const forced = forceKey
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean)
        .map(
          (k) =>
            SCENARIOS.find((s) => s.experiment === k || s.aliases?.includes(k))
              ?.experiment ?? k
        );
      localStorage.setItem("forceExperiment", forced.join(","));
      localStorage.setItem("forceArm", arm);
      return;
    }

    const key = params.get("debug");
    if (!key) return;
    const scenario = SCENARIOS.find(
      (s) => s.experiment === key || s.aliases?.includes(key)
    );

    if (!scenario) {
      // Unknown key: treat it as an experiment name and just force the arm.
      // The app picks it up via getAssignedExperimentArm on next render.
      localStorage.setItem("forceExperiment", key);
      localStorage.setItem("forceArm", arm);
      return;
    }

    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        // currentPageIndex must be truthy for the restore path to fire; actual
        // navigation resolves by name against the live model.
        currentPageIndex: 1,
        currentPageName: scenario.currentPageName,
        data: scenario.data,
      })
    );
    // skip the landing loading animation
    localStorage.setItem("disable_landing_loading", "true");
    // force the experiment arm on (no Statsig assignment locally)
    localStorage.setItem("forceExperiment", scenario.experiment);
    localStorage.setItem("forceArm", arm);

    if (scenario.redirectTo && window.location.pathname !== scenario.redirectTo) {
      window.location.replace(
        `${scenario.redirectTo}?debug=${encodeURIComponent(key)}&arm=${encodeURIComponent(arm)}`
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[debugExperiment] failed to activate", e);
  }
}
